import { LevelType } from '../types/Level';
import { AIError, ClaudeRefusalError } from '../ai/types';
import { DraftLevel } from './types';
import { JobQueueState, jobStateOf } from './jobQueue';

// A fun fact for a draft level: one Claude call that searches the web, and a check in code that the
// page Claude names as its source is one the search really returned.

export const FACT_FLAG_PREFIX = 'Fun fact';

// Claude answered, but had nothing worth telling. It concerns this level only, so the other levels go on.
export class NoFactError extends AIError {
  constructor() {
    super('claude', 'Claude found no fun fact worth telling for this level.');
    Object.setPrototypeOf(this, NoFactError.prototype);
  }
}

// What the player has just solved, in words Claude can search for.
export const factSubject = (draft: DraftLevel): string => {
  const context = (draft.context || '').trim();
  switch (draft.levelType) {
    case LevelType.WhatsTheSong:
      return `the Israeli song "${draft.sentence || ''}"${context ? ` by ${context}` : ''}`;
    case LevelType.KnownSentence:
      return `the Hebrew saying "${draft.sentence || ''}"${context ? ` (meaning: ${context})` : ''}`;
    case LevelType.WhereIsThisLocated:
      return `the place "${draft.locationName || ''}" in ${draft.sentence || 'Israel'}${context ? ` (${context})` : ''}`;
    case LevelType.CategoryWords:
    case LevelType.WordSearch:
      return `the category "${draft.category || ''}", whose words are: ${(draft.words || []).join(', ')}`;
    default:
      return `"${draft.sentence || ''}"${context ? ` (${context})` : ''}`;
  }
};

export const buildFunFactPrompt = (draft: DraftLevel, previousFact: string = ''): string => {
  const previous = previousFact.trim();
  return `Write a fun fact for a level of "RoadIL", a Hebrew puzzle game for an Israeli audience. The player reads it right after solving the level.

The level is about: ${factSubject(draft)}

Use web search to find a fact that is true, specific and surprising: something most Israelis do not know, not a dictionary definition. Open the page you rely on. Do not write the fact from memory.

Rules for the fact:
- Hebrew without niqqud, 1 to 2 sentences, at most 200 characters.
- No intro, no quotation marks around it, no emojis, no markdown and no reference marks such as [1].
- It is about this exact subject, and a page you opened in this search says it.${previous ? `
- It must be a different fact from this one, which is being replaced: ${previous}` : ''}

Output exactly this format and nothing else:
FACT: <the fact>
SOURCE: <the full address of the one page that says it, starting with https://>

If you have a good fact but no page you opened says it, output this instead:
FACT: <the fact>
UNVERIFIED

If there is nothing worth telling, output exactly: NO_FACT`;
};

export interface ParsedFact {
  fact: string;
  source: string | null;
}

const cleanFact = (text: string): string =>
  text
    .replace(/\*\*/g, '')
    .replace(/\[\d+\]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^["'“”״]+|["'“”״]+$/g, '')
    .trim();

const count = (text: string, char: string): number => text.split(char).length - 1;

// The first web address in the text, without the punctuation or brackets that only surround it.
const firstWebAddress = (text: string): string | null => {
  // Quote marks stay in: Hebrew page titles are full of geresh and gershayim (צ'יפס, צה"ל)
  const match = text.match(/(["']?)(https?:\/\/[^\s<>`]+)/i);
  if (!match) return null;
  const quote = match[1];
  let url = match[2].replace(/[.,;*]+$/, '');
  // A quote mark that was opened just before the address only wraps it
  if (quote && url.slice(-1) === quote) url = url.slice(0, -1).replace(/[.,;*]+$/, '');
  // A closing bracket is part of the address only when it closes one opened inside it (as in Wikipedia titles)
  while (url.slice(-1) === ')' && count(url, ')') > count(url, '(')) {
    url = url.slice(0, -1).replace(/[.,;*]+$/, '');
  }
  return url;
};

export const parseFunFact = (text: string): ParsedFact => {
  const factAt = text.lastIndexOf('FACT:');
  // "NO_FACT:" ends with the same letters as the marker; it is the "nothing to tell" answer, not a fact
  if (factAt >= 3 && text.slice(factAt - 3, factAt) === 'NO_') throw new NoFactError();
  if (factAt < 0) {
    if (text.indexOf('NO_FACT') !== -1) throw new NoFactError();
    throw new AIError('claude', 'Claude did not return a fun fact. Retry.', true);
  }

  const body = text.slice(factAt + 'FACT:'.length);
  const sourceAt = body.indexOf('SOURCE:');
  const unverifiedAt = body.indexOf('UNVERIFIED');
  const ends = [sourceAt, unverifiedAt].filter(index => index >= 0);

  const fact = cleanFact(ends.length > 0 ? body.slice(0, Math.min(...ends)) : body);
  if (!fact) throw new AIError('claude', 'Claude did not return a fun fact. Retry.', true);

  const source = sourceAt >= 0 ? firstWebAddress(body.slice(sourceAt + 'SOURCE:'.length)) : null;
  return { fact, source };
};

// Only these may ever be shown as a link.
export const isWebAddress = (url: string): boolean => /^https?:\/\//i.test(url);

// The same page can be written in several ways; compare what identifies it.
const pageKey = (url: string): string => {
  let value = url.trim();
  try {
    value = decodeURI(value);
  } catch {
    // Not valid percent-encoding: compare it as written
  }
  return value
    .replace(/#.*$/, '')
    .replace(/^https?:\/\/(www\.)?/i, '')
    .replace(/\/+$/, '')
    .toLowerCase();
};

export const isSearchedSource = (source: string, searched: string[]): boolean => {
  const key = pageKey(source);
  return searched.some(url => pageKey(url) === key);
};

export const sourceLabel = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

export type FactResearch = (prompt: string) => Promise<{ text: string; sources: string[] }>;

export interface FactResult {
  funFact: string;
  funFactSource?: string;
  // Warnings for the card; empty when the fact is confirmed by a page Claude opened
  flags: string[];
}

// One billed call. It is never repeated here: a failure goes to the caller as it is.
export const makeFunFact = async (draft: DraftLevel, research: FactResearch): Promise<FactResult> => {
  const reply = await research(buildFunFactPrompt(draft, draft.funFact || ''));
  const parsed = parseFunFact(reply.text);

  if (!parsed.source) {
    return {
      funFact: parsed.fact,
      flags: [`${FACT_FLAG_PREFIX} was not confirmed by a web page. Check it yourself before approving.`],
    };
  }
  if (reply.sources.length === 0) {
    // Nothing to compare against: either Claude did not search, or the search did not say which pages it found
    return {
      funFact: parsed.fact,
      funFactSource: parsed.source,
      flags: [`${FACT_FLAG_PREFIX} source could not be checked, because the search reported no pages. Open the link and check it before approving.`],
    };
  }
  if (!isSearchedSource(parsed.source, reply.sources)) {
    return {
      funFact: parsed.fact,
      funFactSource: parsed.source,
      flags: [`${FACT_FLAG_PREFIX} source is not a page Claude opened. Open the link and check it before approving.`],
    };
  }
  return { funFact: parsed.fact, funFactSource: parsed.source, flags: [] };
};

const withoutFactFlags = (flags: string[]): string[] => flags.filter(flag => flag.indexOf(FACT_FLAG_PREFIX) !== 0);

export const hasFactWarning = (draft: DraftLevel): boolean => draft.flags.some(flag => flag.indexOf(FACT_FLAG_PREFIX) === 0);

export const withFunFact = (draft: DraftLevel, result: FactResult): DraftLevel => {
  const updated: DraftLevel = {
    ...draft,
    funFact: result.funFact,
    flags: [...withoutFactFlags(draft.flags), ...result.flags],
  };
  if (result.funFactSource) updated.funFactSource = result.funFactSource; else delete updated.funFactSource;
  return updated;
};

export const withoutFunFact = (draft: DraftLevel): DraftLevel => {
  const updated: DraftLevel = { ...draft, flags: withoutFactFlags(draft.flags) };
  delete updated.funFact;
  delete updated.funFactSource;
  return updated;
};

// A failure every remaining fact would hit too: Claude rejecting the request outright (bad key, no credit,
// web search not enabled). A rate limit passes; "nothing worth telling" and a declined subject are about one level only.
export const isFatalFactError = (err: unknown): boolean =>
  err instanceof AIError && err.provider === 'claude' && !err.retryable &&
  !(err instanceof NoFactError) && !(err instanceof ClaudeRefusalError);

// The cards "Add missing fun facts" may start: no fact yet, no fact job waiting or running, and nothing else
// happening to the card. A card that is being approved is on its way out, so a fact for it would be paid for and lost.
export const draftsMissingFact = (
  drafts: DraftLevel[],
  factJobs: JobQueueState,
  busy: Record<string, string>
): DraftLevel[] =>
  drafts.filter(draft => !draft.funFact && !busy[draft.id] && jobStateOf(factJobs, draft.id) === 'idle');
